import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Download, Check, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { orderService } from '../services/api';
import toast from 'react-hot-toast';

const Checkout = () => {
  const navigate = useNavigate();
  const { items, getTotalPrice, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Por favor completa todos los campos');
      return false;
    }
    if (!formData.email.includes('@')) {
      toast.error('Por favor ingresa un email válido');
      return false;
    }
    if (formData.phone.length < 10) {
      toast.error('Por favor ingresa un número de teléfono válido');
      return false;
    }
    return true;
  };

  const generateReceipt = () => {
    const receipt = `
COMPROBANTE DE RESERVA
======================

Fecha: ${new Date().toLocaleDateString()}
Hora: ${new Date().toLocaleTimeString()}
Número de Orden: ${orderId}

DATOS DEL CLIENTE:
------------------
Nombre: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Teléfono: ${formData.phone}

PRODUCTOS RESERVADOS:
--------------------
${orderItems.map(item => {
  const price = item.discount_percentage > 0 
    ? item.price * (1 - item.discount_percentage / 100)
    : item.price;
  return `${item.name} - ${item.size} - ${item.color}
Cantidad: ${item.quantity}
Precio unitario: $${price.toFixed(2)}
Subtotal: $${(price * item.quantity).toFixed(2)}
`;
}).join('\n')}

TOTAL A PAGAR: $${orderTotal.toFixed(2)}

INSTRUCCIONES:
--------------
- Esta es una RESERVA de productos
- Nos pondremos en contacto contigo para coordinar el pago y retiro
- Guarda este comprobante para futuras referencias
- Tiempo de reserva: 48 horas

¡Gracias por tu compra en Moda Store!
    `;
    
    const blob = new Blob([receipt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comprobante-${orderId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }

    setLoading(true);
    
    try {
      // Guardar datos antes de limpiar el carrito
      const totalAmount = getTotalPrice();
      const orderItemsData = [...items];
      
      const orderData = {
        customer_name: formData.firstName,
        customer_lastname: formData.lastName,
        customer_email: formData.email,
        customer_phone: formData.phone,
        items: items.map(item => ({
          product_id: item.id,
          product_name: item.name,
          size: item.size,
          color: item.color,
          quantity: item.quantity,
          price: item.discount_percentage > 0 
            ? item.price * (1 - item.discount_percentage / 100)
            : item.price
        })),
        total_amount: totalAmount,
        status: 'pendiente'
      };

      const response = await orderService.create(orderData);
      
      // Guardar los datos para el comprobante
      setOrderId(response.data.id);
      setOrderTotal(totalAmount);
      setOrderItems(orderItemsData);
      setOrderCompleted(true);
      
      toast.success('¡Reserva realizada exitosamente!');
      clearCart();
      
    } catch (error) {
      toast.error('Error al procesar la reserva');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemPrice = (item) => {
    return item.discount_percentage > 0 
      ? item.price * (1 - item.discount_percentage / 100)
      : item.price;
  };

  if (items.length === 0 && !orderCompleted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Tu carrito está vacío</h2>
          <p className="text-gray-600 mb-6">
            Agrega algunos productos antes de proceder al checkout
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Volver a la tienda
          </motion.button>
        </div>
      </div>
    );
  }

  if (orderCompleted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto text-center"
        >
          <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ¡Reserva Confirmada!
          </h2>
          
          <p className="text-gray-600 mb-6">
            Tu reserva ha sido procesada exitosamente. Nos pondremos en contacto contigo 
            para coordinar el pago y retiro de los productos.
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-600 mb-2">Número de orden:</p>
            <p className="font-mono text-lg font-bold text-gray-800">{orderId}</p>
          </div>
          
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={generateReceipt}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Descargar Comprobante</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="w-full py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Volver a la tienda
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          Finalizar Reserva
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Form */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              Información del Cliente
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Tu apellido"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Tu número de teléfono"
                    required
                  />
                </div>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Procesando...' : 'Confirmar Reserva'}
              </motion.button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              Resumen de Reserva
            </h2>
            
            <div className="space-y-4 mb-6">
              {items.map((item) => (
                <div key={`${item.id}-${item.size}-${item.color}`} className="flex items-center space-x-3">
                  <img
                    src={item.images?.[0] || '/api/placeholder/60/60'}
                    alt={item.name}
                    className="w-12 h-12 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">{item.name}</h4>
                    <p className="text-sm text-gray-600">{item.size} • {item.color}</p>
                    <p className="text-sm text-gray-600">Cantidad: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-600">
                      ${(getItemPrice(item) * item.quantity).toFixed(2)}
                    </p>
                    {item.discount_percentage > 0 && (
                      <p className="text-sm text-gray-500 line-through">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-gray-800">Total:</span>
                <span className="text-2xl font-bold text-purple-600">
                  ${getTotalPrice().toFixed(2)}
                </span>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Esta es una reserva. Nos pondremos en contacto 
                  contigo para coordinar el pago y retiro de los productos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Checkout;